// Localization is based on '@ngx-translate/core';
// Please be familiar with official documentations first => https://github.com/ngx-translate/core

import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import 'moment/locale/tr';

export interface Locale {
  lang: string;
  data: any;
}

const LOCALIZATION_LOCAL_STORAGE_KEY = 'language';
const SUPPORTED_LANGUAGES = ['en', 'tr'] as const;
const DEFAULT_LANGUAGE = 'tr';

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  // Private properties
  private langIds: any = [];

  constructor(private translate: TranslateService) {
    // add new langIds to the list
    this.translate.addLangs([DEFAULT_LANGUAGE]);

    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang(DEFAULT_LANGUAGE);
  }

  loadTranslations(...args: Locale[]): void {
    const locales = [...args];

    locales.forEach((locale) => {
      // use setTranslation() with the third argument set to true
      // to append translations instead of replacing them
      this.translate.setTranslation(locale.lang, locale.data, true);
      this.langIds.push(locale.lang);
    });

    // add new languages to the list
    this.translate.addLangs(this.langIds);
    const selectedLanguage = this.getSelectedLanguage();
    this.translate.use(selectedLanguage);
    this.syncMomentLocale(selectedLanguage);
  }

  setLanguage(lang: string) {
    if (!lang || !SUPPORTED_LANGUAGES.includes(lang as (typeof SUPPORTED_LANGUAGES)[number])) {
      return;
    }
    this.translate.use(lang);
    this.syncMomentLocale(lang);
    localStorage.setItem(LOCALIZATION_LOCAL_STORAGE_KEY, lang);
    this.updateSplashLoadingText();
  }

  updateSplashLoadingText(): void {
    const label = document.getElementById('splash-loading-text');
    if (label) {
      label.textContent = this.translate.instant('SPLASH.LOADING');
    }
  }

  /**
   * Returns selected language
   */
  getSelectedLanguage(): string {
    const stored = localStorage.getItem(LOCALIZATION_LOCAL_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored as (typeof SUPPORTED_LANGUAGES)[number])) {
      return stored;
    }
    return this.translate.getDefaultLang();
  }

  private syncMomentLocale(lang: string): void {
    moment.locale(lang === 'tr' ? 'tr' : 'en');
  }
}
